import { supabase } from '../supabase';

export type AppPushDestinoTipo = 'none' | 'ruta' | 'url';
export type AppPushAudiencia = 'todos' | 'usuario' | 'segmento';
export type AppPushPlataformas = 'ambas' | 'android' | 'ios';
export type AppPushEstado = 'borrador' | 'programada' | 'enviando' | 'enviada' | 'cancelada' | 'error';
export type AppPushPlatform = 'android' | 'ios' | 'web' | 'unknown';

export type AppPushSettings = {
  id: number;
  push_habilitado: boolean;
  permiso_titulo: string;
  permiso_cuerpo: string;
  updated_at: string;
};

export type AppPushCampaign = {
  id: string;
  titulo: string;
  cuerpo: string;
  imagen_url: string | null;
  destino_tipo: AppPushDestinoTipo;
  destino_valor: string;
  audiencia: AppPushAudiencia;
  target_user_id: string | null;
  target_user_email: string | null;
  plataformas: AppPushPlataformas;
  programado_en: string | null;
  filtro_ciudad_ids: number[];
  filtro_edad_min: number | null;
  filtro_edad_max: number | null;
  filtro_edicion_id: number | null;
  filtro_votos_min: number | null;
  activo: boolean;
  estado: AppPushEstado;
  error_message: string | null;
  enviado_en: string | null;
  tokens_enviados: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export type AppPushCampaignInput = {
  titulo: string;
  cuerpo: string;
  imagen_url?: string | null;
  destino_tipo?: AppPushDestinoTipo;
  destino_valor?: string;
  audiencia?: AppPushAudiencia;
  target_user_id?: string | null;
  target_user_email?: string | null;
  plataformas?: AppPushPlataformas;
  programado_en?: string | null;
  filtro_ciudad_ids?: number[];
  filtro_edad_min?: number | null;
  filtro_edad_max?: number | null;
  filtro_edicion_id?: number | null;
  filtro_votos_min?: number | null;
  activo?: boolean;
  estado?: AppPushEstado;
};

export type AppPushUserHit = {
  id: string;
  email: string | null;
  nombre: string;
};

function mapCampaign(row: Record<string, unknown>): AppPushCampaign {
  return {
    id: String(row.id ?? ''),
    titulo: String(row.titulo ?? ''),
    cuerpo: String(row.cuerpo ?? ''),
    imagen_url: row.imagen_url != null ? String(row.imagen_url) : null,
    destino_tipo: (String(row.destino_tipo ?? 'none') as AppPushDestinoTipo) || 'none',
    destino_valor: String(row.destino_valor ?? ''),
    audiencia: (String(row.audiencia ?? 'todos') as AppPushAudiencia) || 'todos',
    target_user_id: row.target_user_id != null ? String(row.target_user_id) : null,
    target_user_email: row.target_user_email != null ? String(row.target_user_email) : null,
    plataformas: (String(row.plataformas ?? 'ambas') as AppPushPlataformas) || 'ambas',
    programado_en: row.programado_en != null ? String(row.programado_en) : null,
    filtro_ciudad_ids: Array.isArray(row.filtro_ciudad_ids)
      ? (row.filtro_ciudad_ids as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
      : [],
    filtro_edad_min: row.filtro_edad_min != null ? Number(row.filtro_edad_min) : null,
    filtro_edad_max: row.filtro_edad_max != null ? Number(row.filtro_edad_max) : null,
    filtro_edicion_id: row.filtro_edicion_id != null ? Number(row.filtro_edicion_id) : null,
    filtro_votos_min: row.filtro_votos_min != null ? Number(row.filtro_votos_min) : null,
    activo: row.activo !== false,
    estado: (String(row.estado ?? 'borrador') as AppPushEstado) || 'borrador',
    error_message: row.error_message != null ? String(row.error_message) : null,
    enviado_en: row.enviado_en != null ? String(row.enviado_en) : null,
    tokens_enviados: Number(row.tokens_enviados ?? 0) || 0,
    created_by: row.created_by != null ? String(row.created_by) : null,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

export async function fetchAppPushSettings(): Promise<AppPushSettings> {
  const { data, error } = await supabase
    .from('app_push_settings')
    .select('*')
    .eq('id', 1)
    .maybeSingle();
  if (error) throw error;
  if (!data) {
    return {
      id: 1,
      push_habilitado: true,
      permiso_titulo: 'Activa las notificaciones',
      permiso_cuerpo:
        'Te avisaremos de nuevas ediciones, recordatorios de votación y novedades.',
      updated_at: new Date().toISOString(),
    };
  }
  return {
    id: 1,
    push_habilitado: data.push_habilitado !== false,
    permiso_titulo: String(data.permiso_titulo ?? ''),
    permiso_cuerpo: String(data.permiso_cuerpo ?? ''),
    updated_at: String(data.updated_at ?? ''),
  };
}

export async function updateAppPushSettings(input: {
  push_habilitado: boolean;
  permiso_titulo: string;
  permiso_cuerpo: string;
}): Promise<AppPushSettings> {
  const { data, error } = await supabase
    .from('app_push_settings')
    .update({
      push_habilitado: input.push_habilitado,
      permiso_titulo: input.permiso_titulo.trim() || 'Activa las notificaciones',
      permiso_cuerpo:
        input.permiso_cuerpo.trim() ||
        'Te avisaremos de nuevas ediciones, recordatorios de votación y novedades.',
      updated_at: new Date().toISOString(),
    })
    .eq('id', 1)
    .select('*')
    .single();
  if (error) throw error;
  return {
    id: 1,
    push_habilitado: data.push_habilitado !== false,
    permiso_titulo: String(data.permiso_titulo ?? ''),
    permiso_cuerpo: String(data.permiso_cuerpo ?? ''),
    updated_at: String(data.updated_at ?? ''),
  };
}

export async function listAppPushCampaigns(limit = 40): Promise<AppPushCampaign[]> {
  const { data, error } = await supabase
    .from('app_push_campaigns')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((r) => mapCampaign(r as Record<string, unknown>));
}

export async function createAppPushCampaign(
  input: AppPushCampaignInput,
  createdBy?: string | null,
): Promise<AppPushCampaign> {
  const payload = {
    titulo: input.titulo.trim(),
    cuerpo: input.cuerpo.trim(),
    imagen_url: input.imagen_url?.trim() || null,
    destino_tipo: input.destino_tipo ?? 'none',
    destino_valor: (input.destino_valor ?? '').trim(),
    audiencia: input.audiencia ?? 'todos',
    target_user_id: input.target_user_id ?? null,
    target_user_email: input.target_user_email?.trim() || null,
    plataformas: input.plataformas ?? 'ambas',
    programado_en: input.programado_en || null,
    filtro_ciudad_ids: input.filtro_ciudad_ids ?? [],
    filtro_edad_min: input.filtro_edad_min ?? null,
    filtro_edad_max: input.filtro_edad_max ?? null,
    filtro_edicion_id: input.filtro_edicion_id ?? null,
    filtro_votos_min: input.filtro_votos_min ?? null,
    activo: input.activo !== false,
    estado: input.estado ?? 'borrador',
    created_by: createdBy ?? null,
    updated_at: new Date().toISOString(),
  };
  if (!payload.titulo || !payload.cuerpo) {
    throw new Error('Título y cuerpo son obligatorios');
  }
  const { data, error } = await supabase
    .from('app_push_campaigns')
    .insert(payload)
    .select('*')
    .single();
  if (error) throw error;
  return mapCampaign(data as Record<string, unknown>);
}

export async function updateAppPushCampaign(
  id: string,
  input: AppPushCampaignInput,
): Promise<AppPushCampaign> {
  const payload = {
    titulo: input.titulo.trim(),
    cuerpo: input.cuerpo.trim(),
    imagen_url: input.imagen_url?.trim() || null,
    destino_tipo: input.destino_tipo ?? 'none',
    destino_valor: (input.destino_valor ?? '').trim(),
    audiencia: input.audiencia ?? 'todos',
    target_user_id: input.target_user_id ?? null,
    target_user_email: input.target_user_email?.trim() || null,
    plataformas: input.plataformas ?? 'ambas',
    programado_en: input.programado_en || null,
    filtro_ciudad_ids: input.filtro_ciudad_ids ?? [],
    filtro_edad_min: input.filtro_edad_min ?? null,
    filtro_edad_max: input.filtro_edad_max ?? null,
    filtro_edicion_id: input.filtro_edicion_id ?? null,
    filtro_votos_min: input.filtro_votos_min ?? null,
    activo: input.activo !== false,
    estado: input.estado ?? 'borrador',
    updated_at: new Date().toISOString(),
  };
  if (!payload.titulo || !payload.cuerpo) {
    throw new Error('Título y cuerpo son obligatorios');
  }
  const { data, error } = await supabase
    .from('app_push_campaigns')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return mapCampaign(data as Record<string, unknown>);
}

export async function deleteAppPushCampaign(id: string): Promise<void> {
  const { error } = await supabase.from('app_push_campaigns').delete().eq('id', id);
  if (error) throw error;
}

/** Busca usuarios por email o nombre (para pruebas a un destinatario). */
export async function searchAppPushUsers(query: string): Promise<AppPushUserHit[]> {
  const rawSearch = query.trim();
  if (rawSearch.length < 2) return [];

  // PostgREST: `*` = comodín. Evitar `%` / comillas / paréntesis que rompen el `.or()`.
  const q = rawSearch
    .replace(/[%*,()]/g, ' ')
    .replace(/"/g, '')
    .trim()
    .replace(/\s+/g, '*');
  if (!q) return [];

  const pattern = `*${q}*`;
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, email, nombre, apellido')
    .or(
      [`email.ilike.${pattern}`, `nombre.ilike.${pattern}`, `apellido.ilike.${pattern}`].join(','),
    )
    .order('fecha_creacion', { ascending: false })
    .limit(12);

  if (error) throw error;

  return (data ?? []).map((p) => {
    const row = p as {
      id: string;
      email?: string | null;
      nombre?: string | null;
      apellido?: string | null;
    };
    return {
      id: String(row.id),
      email: row.email ?? null,
      nombre: [row.nombre, row.apellido].filter(Boolean).join(' ').trim() || 'Sin nombre',
    };
  });
}

export async function upsertAppPushDeviceToken(input: {
  userId: string;
  expoPushToken: string;
  platform?: AppPushPlatform;
}): Promise<void> {
  const token = input.expoPushToken.trim();
  if (!token) throw new Error('Token vacío');
  if (token.length < 20) {
    throw new Error('El token no parece un FCM token válido');
  }
  const platform = input.platform ?? 'unknown';
  // Si ya existe el mismo token, actualizar plataforma; si hay otro token del user, insertar
  const { error } = await supabase.from('app_push_device_tokens').upsert(
    {
      user_id: input.userId,
      expo_push_token: token,
      platform,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,expo_push_token' },
  );
  if (error) throw error;
}

export async function countTokensForUser(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('app_push_device_tokens')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId);
  if (error) throw error;
  return count ?? 0;
}

/**
 * Envía la campaña ahora vía Edge Function → FCM.
 * Requiere tokens FCM registrados (app o token de prueba en dashboard).
 */
/** Envía las campañas con estado programada cuya hora ya pasó. */
export async function dispatchDueAppPushCampaigns(): Promise<number> {
  const { data, error } = await supabase.functions.invoke('send-push-campaign', {
    body: { dispatchDue: true },
  });
  if (error) throw error;
  if (data?.error) throw new Error(String(data.error));
  return Number(data?.dispatched ?? 0) || 0;
}

export async function sendAppPushCampaignNow(campaignId: string): Promise<AppPushCampaign> {
  const settings = await fetchAppPushSettings();
  if (!settings.push_habilitado) {
    throw new Error('Las notificaciones push están deshabilitadas en ajustes');
  }

  const { data, error } = await supabase.functions.invoke('send-push-campaign', {
    body: { campaignId },
  });

  if (error) {
    const ctx = (error as { context?: { body?: string; json?: () => Promise<unknown> } }).context;
    let detail = error.message;
    try {
      if (ctx && typeof (ctx as Response).json === 'function') {
        const j = (await (ctx as Response).json()) as { error?: string };
        if (j?.error) detail = j.error;
      }
    } catch {
      /* ignore */
    }
    throw new Error(detail || 'No se pudo enviar el push');
  }

  if (data?.error) {
    throw new Error(String(data.error));
  }

  const row = (data?.campaign ?? null) as Record<string, unknown> | null;
  if (!row) {
    // Recargar desde BD si la función no devolvió la fila
    const { data: refreshed, error: refErr } = await supabase
      .from('app_push_campaigns')
      .select('*')
      .eq('id', campaignId)
      .single();
    if (refErr) throw refErr;
    return mapCampaign(refreshed as Record<string, unknown>);
  }

  return mapCampaign(row);
}
